import { useState, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from '@tanstack/react-router';
import { Button } from '../../Button';
import Input from '../../Input';
import FormLabel from '../FormLabel';

interface LoginStepProps {
  formData: {
    email: string;
    password: string;
  };
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  canProceed: boolean;
}

export default function LoginStep({ formData, onInputChange, canProceed }: LoginStepProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string>('');
  // サーバーからのリダイレクト時に渡されたエラーメッセージ（reCAPTCHA失敗・ログイン失敗など）
  const [serverError, setServerError] = useState<string>('');
  // クライアント側エラー（reCAPTCHA取得失敗など）
  const [clientError, setClientError] = useState<string>('');

  // useGoogleReCaptchaフックを使用（GoogleReCaptchaProviderの内側で使用されることを前提）
  // Providerの外で使用された場合はエラーが発生するが、LoginTemplateでProviderをラップしているため問題なし
  const { executeRecaptcha } = useGoogleReCaptcha();

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  useEffect(() => {
    // サーバーが埋め込んだエラーメッセージ（script#flash-alert の JSON）を取得
    const flashEl = document.getElementById('flash-alert');
    if (flashEl?.textContent?.trim()) {
      try {
        const msg = JSON.parse(flashEl.textContent.trim()) as string;
        if (msg) setServerError(msg);
      } catch {
        // JSON でない場合はそのまま表示（Slim の仕様に合わせる）
        setServerError(flashEl.textContent.trim());
      }
    }

    // HTMLメタタグからCSRFトークンを取得
    const metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    if (metaTag) {
      setCsrfToken(metaTag.content);
    } else {
      // メタタグがない場合はAPIから取得
      const fetchCSRFToken = async () => {
        try {
          const response = await fetch('/api/csrf_token');
          if (response.ok) {
            const data = await response.json();
            setCsrfToken(data.authenticity_token);
          }
        } catch (error) {
          console.error('CSRF token取得エラー:', error);
        }
      };
      fetchCSRFToken();
    }
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!canProceed) {
      return;
    }

    const form = e.currentTarget;

    // reCAPTCHAトークンを取得
    if (executeRecaptcha) {
      try {
        const token = await executeRecaptcha('login');

        // トークンを取得したら、hiddenフィールドを追加してフォームを送信
        // recaptcha gemはデフォルトで'g-recaptcha-response'というパラメータ名を期待する
        const tokenInput = document.createElement('input');
        tokenInput.type = 'hidden';
        tokenInput.name = 'g-recaptcha-response';
        tokenInput.value = token;
        form.appendChild(tokenInput);

        // フォームを送信
        form.submit();
      } catch (error) {
        console.error('reCAPTCHAエラー:', error);
        setClientError('認証の準備に失敗しました。ページを再読み込みして再度お試しください。');
      }
    } else {
      form.submit();
    }
  };

  return (
    <>
      <div className="flex size-full flex-col items-center justify-center">
        {/* Title Section */}
        <div className="mb-12 text-center">
          <div className="flex flex-col items-center justify-center gap-3">
            <img src="/agent/product-logo.png" alt="Agent Hub Logo" className="h-10 w-auto" />
            <div className="text-base font-normal text-black">へようこそ！</div>
          </div>
        </div>

        {/* エラーメッセージ（サーバーからのリダイレクト時 or クライアントエラー） */}
        {(serverError || clientError) && (
          <div
            className="mb-6 w-full max-w-md rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            role="alert"
          >
            {serverError || clientError}
          </div>
        )}

        {/* Form Section */}
        <form
          action="/agent/sign_in"
          method="post"
          className="w-full max-w-md space-y-6"
          onSubmit={handleFormSubmit}
        >
          {/* CSRF Token */}
          <input type="hidden" name="authenticity_token" value={csrfToken} />

          {/* Email Field */}
          <div>
            <FormLabel>メールアドレス</FormLabel>
            <Input
              type="email"
              name="organization_user[email]"
              className="w-full"
              value={formData.email}
              onChange={onInputChange}
              placeholder=""
              required
            />
          </div>

          {/* Password Field */}
          <div>
            <FormLabel>ログインパスワード</FormLabel>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="organization_user[password]"
                value={formData.password}
                onChange={onInputChange}
                className="w-full pr-10"
                required
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute top-1/2 right-3 -translate-y-1/2 transform bg-white"
              >
                {showPassword ? (
                  <img
                    src="/agent/icon-eye-visible.svg"
                    alt="パスワードを非表示にする"
                    width="24"
                    height="24"
                  />
                ) : (
                  <img
                    src="/agent/icon-eye-hidden.svg"
                    alt="パスワードを表示する"
                    width="24"
                    height="24"
                  />
                )}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mx-auto w-[320px] pt-6">
            <Button type="submit" disabled={!canProceed} color="primary" size="lg" full>
              ログインする
            </Button>
            <div className="mt-4 text-center">
              <Link
                to="/account/password/setting"
                className="text-sm text-blue-600 hover:underline"
              >
                パスワードをお忘れの方
              </Link>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
